import type { Message, SendMessageRequest } from '@repo/api';
import { snakeCaseKeysDeep, camelCaseKeysDeep, type WebSocketStatus } from '@repo/utils';
import { CONTENT_TYPES, MESSAGE_TYPES } from '@repo/utils';

interface WebSocketConfig {
  url: string;
  token: string;
  onMessage: (message: Message) => void;
  onStatusChange: (status: WebSocketStatus) => void;
  onError?: (error: Event) => void;
  onBotTyping?: (isTyping: boolean) => void;
  onMessageDeleted?: (messageId: string) => void;
}

export class ChatWebSocketService {
  private ws: WebSocket | null = null;
  private config: WebSocketConfig | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isManualClose = false;
  private sentReadReceipts: Set<string> = new Set();

  constructor() {}

  connect(config: WebSocketConfig) {
    // Don't connect if already open
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    // If connecting, wait a bit and check again
    if (this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    // Disconnect existing connection if any (but not connecting)
    if (this.ws && this.ws.readyState !== WebSocket.CONNECTING) {
      this.ws.close();
      this.ws = null;
    }

    this.config = config;
    this.isManualClose = false;
    this.initializeWebSocket();
  }

  private initializeWebSocket() {
    if (!this.config) {
      return;
    }

    const { url, token, onStatusChange, onMessage, onError, onBotTyping, onMessageDeleted } =
      this.config;

    try {
      onStatusChange('connecting');

      // For web, we pass token as query parameter
      // Use URL constructor to properly add the token (like old production code)
      const socketUrl = new URL(url);
      socketUrl.searchParams.set('one_time_token', token);
      const urlWithToken = socketUrl.toString();

      this.ws = new WebSocket(urlWithToken);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        onStatusChange('connected');
      };

      this.ws.onmessage = event => {
        try {
          if (!event.data) return;

          const rawData = JSON.parse(event.data);
          const data = camelCaseKeysDeep<Record<string, any>>(rawData);

          const message: Message = {
            ...data,
            id: data.id || data._id || data.Id,
            senderType: data.senderType || MESSAGE_TYPES.BOT,
            atCreated: data.atCreated || new Date().toISOString(),
            contentData: data.contentData,
          } as Message;

          if ('id' in message) {
            delete (message as any)._id;
          }

          // Backend controls loading indicator via contentType: 'loading'
          if (message.contentType === CONTENT_TYPES.LOADING) {
            if (onBotTyping) {
              onBotTyping(message.contentMessage as boolean);
            }
            return;
          }

          // Backend retracts a message via contentType: 'message_deleted'. Must
          // return before onMessage, or the frame lands in the transcript as an
          // empty row and gets a read receipt.
          if (message.contentType === CONTENT_TYPES.MESSAGE_DELETED) {
            const deletedId = message.relatedBotMessageId;
            if (onMessageDeleted && deletedId) {
              onMessageDeleted(deletedId);
            }
            return;
          }

          onMessage(message);
        } catch (error) {}
      };

      this.ws.onerror = error => {
        onStatusChange('error');
        if (onError) onError(error);
      };

      this.ws.onclose = event => {
        onStatusChange('disconnected');

        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };
    } catch (error) {
      onStatusChange('error');
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    this.reconnectTimer = setTimeout(() => {
      this.initializeWebSocket();
    }, delay);
  }

  sendMessage(messageData: SendMessageRequest): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket is not connected'));
        return;
      }

      try {
        const payload = snakeCaseKeysDeep({
          ...messageData,
        });

        this.ws.send(JSON.stringify(payload));
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  }

  sendMessageReadReceipt(messageId: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Check if we already sent a receipt for this message
    if (this.sentReadReceipts.has(messageId)) {
      return;
    }

    try {
      const payload = {
        event: 'message_read',
        message_id: messageId,
      };

      this.ws.send(JSON.stringify(payload));
      this.sentReadReceipts.add(messageId);
    } catch (error) {}
  }

  resetAndReconnect() {
    this.reconnectAttempts = 0;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
      this.ws.close();
    }

    if (this.config) {
      this.initializeWebSocket();
    }
  }

  disconnect() {
    this.isManualClose = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      // Detach all handlers BEFORE calling close() so that the async
      // onclose/onerror callbacks never fire after this point. Without this,
      // the old socket's onclose can fire after the new socket has already
      // connected and flip the store status back to 'disconnected'.
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempts = 0;
    this.sentReadReceipts.clear();
    this.config = null;
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getStatus(): WebSocketStatus {
    if (!this.ws) return 'disconnected';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'disconnected';
    }
  }
}

// Singleton instance
let chatWebSocketInstance: ChatWebSocketService | null = null;

export const getChatWebSocket = (): ChatWebSocketService => {
  if (!chatWebSocketInstance) {
    chatWebSocketInstance = new ChatWebSocketService();
  }
  return chatWebSocketInstance;
};
